import type {
  EmployeesWithTimesheets,
  EmployeeWithTimesheets,
} from '@/lib/request/types/employeesWithTimesheets';
import type { Roster } from '@/lib/request/types/roster';
import type { Timesheet } from '@/lib/request/types/timesheet';
import { defineStore } from 'pinia';

import { DateTime } from 'luxon';

import { useTimesheetStore, BreakState } from '@/stores/timesheets';
import { useRosterStore } from '@/stores/rosters';
import { useEmployeeStore, EmployeeWithTimesheet } from '@/stores/employees';
import { toTwelveHourTime } from '@/lib/util/dateTime';

import {
  getLateMinutes,
  getBreakLateMinutes,
  getBreakStartingSoonMinutes,
  getBreakEndingSoonMinutes,
  getStartingSoonMinutes,
  getEndingSoonMinutes,
  getOverTimeMinutes,
  getOnBreakMinutes,
  getBreakOverTimeMinutes,
} from './statusGetters';

export type EmployeeWithTimesheetData = {
  [employeeId: number]: EmployeeWithTimesheets;
};
export interface EmployeeWithTimesheetStore {
  data: EmployeeWithTimesheetData;
  employeesAlphabetically: EmployeesWithTimesheets;
  employeesComingUp: EmployeesWithTimesheets;
  employeesOnShift: EmployeesWithTimesheets;
  employeesNoStatus: EmployeesWithTimesheets;
}

export enum ShiftStatus {
  NoStatus = 0,
  Scheduled,
  OnBreak,
  OnShift,
  Late,
}

export enum ComingUpStatus {
  NoStatus = 0,
  Late,
  StartingSoon,
  BreakLate,
  BreakStartingSoon,
  BreakEndingSoon,
  BreakOver,
  OnBreak,
  EndingSoon,
  OverTime,
}

function employeesToObject(employees: EmployeesWithTimesheets) {
  return employees.reduce((allEmployees, employeeWithTimesheets) => {
    const { Id } = employeeWithTimesheets.employee;
    return { ...allEmployees, [Id]: employeeWithTimesheets };
  }, {});
}

function sortEmployeesAlphabetically(
  employeeWithTimesheets: EmployeesWithTimesheets
) {
  let sortedEmployees = employeeWithTimesheets;
  sortedEmployees = employeeWithTimesheets.sort(
    (a: EmployeeWithTimesheet, b: EmployeeWithTimesheet) => {
      const employeeA = a.employee.DisplayName.toLowerCase();
      const employeeB = b.employee.DisplayName.toLowerCase();

      if (employeeA > employeeB) {
        return 1;
      }
      if (employeeA < employeeB) {
        return -1;
      }
      return 0;
    }
  );
  return sortedEmployees;
}

function sortByMinutes(employeesWithTimesheets: EmployeesWithTimesheets) {
  return employeesWithTimesheets.sort(
    (a: EmployeeWithTimesheets, b: EmployeeWithTimesheets) => {
      const minutesValueA = a.comingUpData.minutes;
      const minutesValueB = b.comingUpData.minutes;

      if (minutesValueA > minutesValueB) {
        return 1;
      }
      if (minutesValueA < minutesValueB) {
        return -1;
      }
      return 0;
    }
  );
}

function sortComingUpEmployees(
  employeesWithTimesheets: EmployeesWithTimesheets
) {
  const minutesSorted = sortByMinutes(employeesWithTimesheets);
  return minutesSorted.sort(
    (a: EmployeeWithTimesheets, b: EmployeeWithTimesheets) => {
      const statusValueA = a.comingUpData.status;
      const statusValueB = b.comingUpData.status;

      if (statusValueA > statusValueB) {
        return 1;
      }
      if (statusValueA < statusValueB) {
        return -1;
      }
      return 0;
    }
  );
}

export const useEmployeeWithTimesheetsStore = defineStore({
  id: 'employeesWithTimesheets',
  state: (): EmployeeWithTimesheetStore => ({
    data: {},
    employeesAlphabetically: [],
    employeesComingUp: [],
    employeesOnShift: [],
    employeesNoStatus: [],
  }),
  getters: {
    getEmployeeWithTimesheetsById(state) {
      return (id: number) => state.data[id];
    },
    getStatusValueForEmployee(): (id: number) => ShiftStatus {
      return (id: number) => {
        const employeeWithTimesheets = this.getEmployeeWithTimesheetsById(id);

        if (
          employeeWithTimesheets.timesheets.length &&
          this.isOnShift(employeeWithTimesheets)
        ) {
          if (this.isOnBreak(employeeWithTimesheets)) {
            return ShiftStatus.OnBreak;
          } else {
            return ShiftStatus.OnShift;
          }
        }

        if (employeeWithTimesheets.rostersNotStarted.length) {
          if (this.isLate(employeeWithTimesheets)) {
            return ShiftStatus.Late;
          } else if (this.isScheduled(id)) {
            return ShiftStatus.Scheduled;
          }
        }

        return ShiftStatus.NoStatus;
      };
    },
    getStartEndTimeLabel() {
      return (id: number) => {
        const employeeWithTimesheets = this.getEmployeeWithTimesheetsById(id);

        const scheduledShift = this.getNextScheduledRoster(id);
        const inProgressTimesheet = employeeWithTimesheets.timesheets.find(
          (timesheet) => {
            return timesheet.IsInProgress;
          }
        );

        const attachedRoster =
          employeeWithTimesheets.rosters?.find((roster) => {
            return roster.Id === inProgressTimesheet?.Roster;
          }) || inProgressTimesheet;

        const timesheet = inProgressTimesheet || scheduledShift;
        let times = [
          timesheet?.StartTimeLocalized,
          timesheet?.EndTimeLocalized,
        ];

        if (attachedRoster) {
          times = [
            attachedRoster?.StartTimeLocalized,
            attachedRoster?.EndTimeLocalized,
          ];
        }

        if (scheduledShift || inProgressTimesheet?.Roster) {
          return times
            .filter((val) => val)
            .map((time) => toTwelveHourTime(time))
            .join(' - ');
        }
        return toTwelveHourTime(times[0]);
      };
    },
    getComingUpDataByEmployeeId() {
      return (id: number) => {
        const employeeWithTimesheets = this.getEmployeeWithTimesheetsById(id);
        const comingupInfo = this.getComingUpData(employeeWithTimesheets);
        if (comingupInfo) {
          return comingupInfo;
        }
      };
    },
    getNextScheduledRoster() {
      return (id: number) => {
        const { rostersNotStarted } =
          this.getEmployeeWithTimesheetsById(id) || {};
        const ACTIVE_ROSTER_MAX_TIME_DIFFERENCE = 8 * 3600;
        const scheduledShift = rostersNotStarted?.filter(
          ({ MatchedByTimesheet, EndTimeLocalized, StartTimeLocalized }) =>
            MatchedByTimesheet === 0 &&
            DateTime.now().toSeconds() -
              DateTime.fromISO(EndTimeLocalized).toSeconds() <
              ACTIVE_ROSTER_MAX_TIME_DIFFERENCE &&
            DateTime.fromISO(StartTimeLocalized).toSeconds() -
              DateTime.now().toSeconds() <
              ACTIVE_ROSTER_MAX_TIME_DIFFERENCE
        );
        if (scheduledShift) {
          return scheduledShift[0] ?? null;
        }
        return null;
      };
    },
    isScheduled() {
      return (id: number) => {
        const SECONDS_IN_HOUR = 3600;
        const HOURS_AWAY_SCHEDULED = 8;
        const SECONDS_UNTIL_LATE = -60;
        const now = DateTime.now().toSeconds();
        const { rostersNotStarted } =
          this.getEmployeeWithTimesheetsById(id) || {};
        let isScheduled = false;
        rostersNotStarted?.some((roster) => {
          const hoursAway =
            (roster.StartTime - now.valueOf()) / SECONDS_IN_HOUR;
          const isLate = !!(
            roster.StartTime - now.valueOf() <
            SECONDS_UNTIL_LATE
          );
          if (!isLate && hoursAway < HOURS_AWAY_SCHEDULED) {
            isScheduled = true;
            return isScheduled;
          }
        });
        return isScheduled;
      };
    },
    isLate() {
      return (employeeWithTimesheets: EmployeeWithTimesheets) => {
        const SECONDS_IN_HOUR = 3600;
        const ACTIVE_ROSTER_MAX_TIME_DIFFERENCE = 8 * SECONDS_IN_HOUR;
        const now = DateTime.now().toSeconds();
        let isLate = false;

        employeeWithTimesheets.rostersNotStarted?.some((roster) => {
          if (
            roster.StartTime - now < 0 &&
            now - roster.EndTime < ACTIVE_ROSTER_MAX_TIME_DIFFERENCE
          ) {
            isLate = true;
            return isLate;
          }
        });
        return isLate;
      };
    },
    isOnShift() {
      return (employeeWithTimesheets: EmployeeWithTimesheets) => {
        return !!employeeWithTimesheets.timesheets.find((timesheet) => {
          return timesheet.IsInProgress;
        });
      };
    },
    isOnBreak() {
      return (employeeWithTimesheets: EmployeeWithTimesheets) => {
        return !!employeeWithTimesheets.timesheets
          .find((timesheet) => timesheet.IsInProgress)
          ?.Slots.find(({ strState }) => {
            return strState == BreakState.InProgress;
          });
      };
    },
    getComingUpData() {
      return (employeeWithTimesheets: EmployeeWithTimesheets) => {
        if (!Array.isArray(employeeWithTimesheets.rostersNotStarted)) {
          return {
            minutes: 0,
            status: ComingUpStatus.NoStatus,
          };
        }

        const { rostersNotStarted, timesheets, rosters } =
          employeeWithTimesheets;

        for (const timesheet of timesheets) {
          if (timesheet.IsInProgress) {
            let attachedRoster: Roster | Timesheet = timesheet;
            if (timesheet.Roster) {
              attachedRoster =
                rosters?.find((roster) => {
                  return roster.Id === timesheet.Roster;
                }) || timesheet;
            }

            const breakStartingLateMinutes = getBreakLateMinutes(
              attachedRoster.Slots
            );
            if (
              typeof breakStartingLateMinutes == 'number' &&
              !this.isOnBreak(employeeWithTimesheets)
            ) {
              return {
                minutes: breakStartingLateMinutes,
                status: ComingUpStatus.BreakLate,
              };
            }

            const breakStartingSoonMinutes = getBreakStartingSoonMinutes(
              attachedRoster.Slots
            );
            if (
              typeof breakStartingSoonMinutes == 'number' &&
              !this.isOnBreak(employeeWithTimesheets)
            ) {
              return {
                minutes: breakStartingSoonMinutes,
                status: ComingUpStatus.BreakStartingSoon,
              };
            }

            const breakEndingSoonMinutes = getBreakEndingSoonMinutes(
              timesheet.Slots
            );
            if (
              typeof breakEndingSoonMinutes == 'number' &&
              this.isOnBreak(employeeWithTimesheets)
            ) {
              return {
                minutes: breakEndingSoonMinutes,
                status: ComingUpStatus.BreakEndingSoon,
              };
            }

            const breakOverMinutes = getBreakOverTimeMinutes(timesheet.Slots);
            if (
              this.isOnBreak(employeeWithTimesheets) &&
              typeof breakOverMinutes == 'number'
            ) {
              return {
                minutes: breakOverMinutes || 0,
                status: ComingUpStatus.BreakOver,
              };
            }

            const onBreakMinutes = getOnBreakMinutes(timesheet.Slots);
            if (this.isOnBreak(employeeWithTimesheets)) {
              return {
                minutes: onBreakMinutes || 0,
                status: ComingUpStatus.OnBreak,
              };
            }

            const overTimeMinutes = getOverTimeMinutes(attachedRoster.EndTime);
            if (overTimeMinutes && timesheet.Roster) {
              return {
                minutes: overTimeMinutes,
                status: ComingUpStatus.OverTime,
              };
            }

            const endingSoonMinutes = getEndingSoonMinutes(
              attachedRoster.EndTime
            );
            if (typeof endingSoonMinutes == 'number' && timesheet.Roster) {
              return {
                minutes: endingSoonMinutes,
                status: ComingUpStatus.EndingSoon,
              };
            }
          }
        }

        for (const roster of rostersNotStarted) {
          const lateByMinutes = getLateMinutes(roster.StartTime);
          if (typeof lateByMinutes == 'number' && lateByMinutes > 0) {
            return {
              minutes: lateByMinutes,
              status: ComingUpStatus.Late,
            };
          }

          const startingSoonMinutes = getStartingSoonMinutes(roster.StartTime);
          if (typeof startingSoonMinutes == 'number') {
            return {
              minutes: startingSoonMinutes,
              status: ComingUpStatus.StartingSoon,
            };
          }
        }
      };
    },
    searchUsers(state) {
      return (searchTerm: string) => {
        const employeesWithTimesheets = state.employeesAlphabetically;

        if (!searchTerm) {
          return employeesWithTimesheets;
        }

        const searchTerms = searchTerm
          .split(' ')
          .map((searchTerm) => searchTerm.trim().toLowerCase());

        return employeesWithTimesheets.filter((employeeObject): boolean => {
          return searchTerms.every((term) =>
            employeeObject.searchableText.includes(term)
          );
        });
      };
    },
  },
  actions: {
    async load() {
      const timesheetsStore = useTimesheetStore();
      const rostersStore = useRosterStore();
      const employeeStore = useEmployeeStore();
      const comingUpArray: EmployeesWithTimesheets = [];
      const onShiftArray: EmployeesWithTimesheets = [];
      const noStatusArray: EmployeesWithTimesheets = [];

      const employeesWithTimesheetsArray = employeeStore.data.map(
        (employee): EmployeeWithTimesheets => {
          const timesheets = timesheetsStore.getTimesheetsByEmployeeId(
            employee.Id
          );
          const rosters = rostersStore.getRostersByEmployeeId(employee.Id);
          const rostersNotStarted = rostersStore.getNextRostersByEmployeeId(
            employee.Id
          );
          const searchableText = [
            employee.FirstName.toLowerCase(),
            employee.LastName.toLowerCase(),
            employee.DisplayName.toLowerCase(),
          ].join(' ');

          const employeeWithTimesheets: EmployeeWithTimesheets = {
            employeeId: employee.Id,
            rostersNotStarted,
            rosters,
            timesheets,
            employee,
            searchableText,
            comingUpData: {
              status: 0,
              minutes: 0,
            },
          };

          const comingupData = this.getComingUpData(employeeWithTimesheets);

          if (comingupData) {
            employeeWithTimesheets.comingUpData = {
              minutes: comingupData.minutes || 0,
              status: comingupData.status,
            };
            comingUpArray.push(employeeWithTimesheets);
          } else if (this.isOnShift(employeeWithTimesheets)) {
            onShiftArray.push(employeeWithTimesheets);
          } else {
            noStatusArray.push(employeeWithTimesheets);
          }

          return employeeWithTimesheets;
        }
      );

      this.data = employeesToObject(employeesWithTimesheetsArray);

      this.employeesComingUp = sortComingUpEmployees(comingUpArray);
      this.employeesOnShift = sortEmployeesAlphabetically(onShiftArray);
      this.employeesNoStatus = sortEmployeesAlphabetically(noStatusArray);

      this.employeesAlphabetically = sortEmployeesAlphabetically(
        employeesWithTimesheetsArray
      );
    },
    computeEmployeeGroups() {
      const comingUpArray: EmployeesWithTimesheets = [];
      const onShiftArray: EmployeesWithTimesheets = [];
      const noStatusArray: EmployeesWithTimesheets = [];

      this.employeesAlphabetically.forEach((employee) => {
        const comingupInfo = this.getComingUpData(employee);

        if (comingupInfo) {
          employee.comingUpData = comingupInfo;
          comingUpArray.push(employee);
        } else if (this.isOnShift(employee)) {
          onShiftArray.push(employee);
        } else {
          noStatusArray.push(employee);
        }
      });

      this.employeesComingUp = sortComingUpEmployees(comingUpArray);
      this.employeesOnShift = sortEmployeesAlphabetically(onShiftArray);
      this.employeesNoStatus = sortEmployeesAlphabetically(noStatusArray);
    },
  },
});
